import React, { useState, useEffect } from "react"
import {
  Card,
  Table,
  Tag,
  Typography,
  Flex,
  Input,
  Button,
  Space,
  Avatar,
  Tooltip,
  Badge,
  Timeline,
  Segmented,
  Empty,
  Spin,
  Row,
  Col,
  Statistic,
  Divider,
  message,
  Select,
  Modal,
} from "antd"
import {
  BranchesOutlined,
  GitlabOutlined,
  GithubOutlined,
  UserOutlined,
  CalendarOutlined,
  SearchOutlined,
  ReloadOutlined,
  CodeOutlined,
  FileAddOutlined,
  FileExcelOutlined,
  EditOutlined,
  ClockCircleOutlined,
  TagOutlined,
  CopyOutlined,
  CheckCircleOutlined,
  SyncOutlined,
  HistoryOutlined,
  UnorderedListOutlined,
  AppstoreOutlined,
  LinkOutlined,
  MessageOutlined,
  ExpandOutlined,
} from "@ant-design/icons"
import useMediaQuery from "@mui/material/useMediaQuery"

const { Title, Text, Paragraph } = Typography
const { Search } = Input

// GitHub Repositories Configuration
const GITHUB_REPOS = [
  {
    id: "vahedgas-ui",
    owner: "amirkarami-dev",
    repo: "vahedgas-ui",
    label: "فرانت‌اند (UI)",
    color: "#1890ff",
    defaultBranch: "GAS-1013",
  },
  {
    id: "vahedgas-api",
    owner: "amirkarami-dev",
    repo: "vahedgas-api",
    label: "بک‌اند (API)",
    color: "#52c41a",
    defaultBranch: "main",
  },
]

// GitHub Token (optional - for private repos or higher rate limits)
const GITHUB_TOKEN = process.env.REACT_APP_GITHUB_TOKEN || ""

const GitCommits = () => {
  const matches_sm = useMediaQuery("(max-width:600px)")
  const [loading, setLoading] = useState(false)
  const [commits, setCommits] = useState([])
  const [branches, setBranches] = useState([])
  const [searchText, setSearchText] = useState("")
  const [viewMode, setViewMode] = useState("table")
  const [selectedRepo, setSelectedRepo] = useState(GITHUB_REPOS[0])
  const [selectedBranch, setSelectedBranch] = useState(GITHUB_REPOS[0].defaultBranch)
  const [perPage, setPerPage] = useState(30)
  const [selectedCommit, setSelectedCommit] = useState(null)
  const [messageModalVisible, setMessageModalVisible] = useState(false)

  // Show full message modal
  const showFullMessage = (commit) => {
    setSelectedCommit(commit)
    setMessageModalVisible(true)
  }

  // Get current repo config
  const currentRepo = GITHUB_REPOS.find(r => r.id === selectedRepo.id) || GITHUB_REPOS[0]

  // Fetch branches from GitHub
  const fetchBranches = async (repo = currentRepo) => {
    try {
      const headers = GITHUB_TOKEN 
        ? { Authorization: `token ${GITHUB_TOKEN}` }
        : {}
      
      const response = await fetch(
        `https://api.github.com/repos/${repo.owner}/${repo.repo}/branches`,
        { headers }
      )
      
      if (response.ok) {
        const data = await response.json()
        setBranches(data.map(b => b.name))
      }
    } catch (error) {
      console.error("Error fetching branches:", error)
    }
  }

  // Fetch commits from GitHub API
  const fetchCommits = async (branch = selectedBranch, repo = currentRepo) => {
    setLoading(true)
    try {
      const headers = GITHUB_TOKEN 
        ? { Authorization: `token ${GITHUB_TOKEN}` }
        : {}

      const response = await fetch(
        `https://api.github.com/repos/${repo.owner}/${repo.repo}/commits?sha=${branch}&per_page=${perPage}`,
        { headers }
      )

      if (!response.ok) {
        throw new Error(`GitHub API error: ${response.status}`)
      }

      const data = await response.json()
      
      // Transform GitHub data to our format
      const transformedCommits = data.map(commit => ({
        id: commit.sha.substring(0, 8),
        hash: commit.sha,
        message: commit.commit.message.split('\n')[0], // First line only
        fullMessage: commit.commit.message,
        author: commit.commit.author.name,
        authorEmail: commit.commit.author.email,
        authorAvatar: commit.author?.avatar_url,
        date: commit.commit.author.date,
        branch: branch,
        repoId: repo.id,
        repoLabel: repo.label,
        type: detectCommitType(commit.commit.message),
        url: commit.html_url,
        filesChanged: 0,
        additions: 0,
        deletions: 0,
      }))

      setCommits(transformedCommits)
      message.success(`${transformedCommits.length} کامیت از ${repo.label} بارگذاری شد`)
    } catch (error) {
      console.error("Error fetching commits:", error)
      message.error("خطا در دریافت کامیت‌ها: " + error.message)
    } finally {
      setLoading(false)
    }
  }

  // Detect commit type from message
  const detectCommitType = (message) => {
    const lowerMessage = message.toLowerCase()
    if (lowerMessage.startsWith("feat") || lowerMessage.includes("feature")) return "feature"
    if (lowerMessage.startsWith("fix") || lowerMessage.includes("bug")) return "fix"
    if (lowerMessage.startsWith("refactor")) return "refactor"
    if (lowerMessage.startsWith("docs") || lowerMessage.includes("readme")) return "docs"
    if (lowerMessage.startsWith("style") || lowerMessage.includes("css")) return "style"
    if (lowerMessage.startsWith("test")) return "test"
    if (lowerMessage.startsWith("chore") || lowerMessage.includes("merge")) return "chore"
    return "other"
  }

  // Initial load
  useEffect(() => {
    fetchBranches(currentRepo)
    fetchCommits(currentRepo.defaultBranch, currentRepo)
  }, [])

  // Reload when repo changes
  const handleRepoChange = (repoId) => {
    const newRepo = GITHUB_REPOS.find(r => r.id === repoId)
    if (newRepo) {
      setSelectedRepo(newRepo)
      setSelectedBranch(newRepo.defaultBranch)
      setBranches([])
      setCommits([])
      fetchBranches(newRepo)
      fetchCommits(newRepo.defaultBranch, newRepo)
    }
  }

  // Reload when branch or perPage changes
  useEffect(() => {
    if (selectedBranch && currentRepo) {
      fetchCommits(selectedBranch, currentRepo)
    }
  }, [selectedBranch, perPage])

  // Filter commits
  const filteredCommits = commits.filter(commit => {
    const matchesSearch =
      commit.message.toLowerCase().includes(searchText.toLowerCase()) ||
      commit.author.toLowerCase().includes(searchText.toLowerCase()) ||
      commit.hash.toLowerCase().includes(searchText.toLowerCase())
    return matchesSearch
  })

  // Get commit type color and icon
  const getCommitTypeConfig = (type) => {
    const config = {
      feature: { color: "#52c41a", icon: <FileAddOutlined />, label: "ویژگی جدید" },
      fix: { color: "#ff4d4f", icon: <CheckCircleOutlined />, label: "رفع باگ" },
      refactor: { color: "#1890ff", icon: <SyncOutlined />, label: "بازنویسی" },
      docs: { color: "#722ed1", icon: <FileExcelOutlined />, label: "مستندات" },
      style: { color: "#fa8c16", icon: <EditOutlined />, label: "استایل" },
      test: { color: "#13c2c2", icon: <CodeOutlined />, label: "تست" },
      chore: { color: "#8c8c8c", icon: <HistoryOutlined />, label: "نگهداری" },
    }
    return config[type] || { color: "#667eea", icon: <CodeOutlined />, label: type }
  }

  // Format date
  const formatDate = (dateString) => {
    const date = new Date(dateString)
    const now = new Date()
    const diff = now - date
    const minutes = Math.floor(diff / 60000)
    const hours = Math.floor(diff / 3600000)
    const days = Math.floor(diff / 86400000)

    if (minutes < 60) return `${minutes} دقیقه پیش`
    if (hours < 24) return `${hours} ساعت پیش`
    if (days < 7) return `${days} روز پیش`
    return date.toLocaleDateString("fa-IR")
  }

  // Copy hash to clipboard
  const copyHash = (hash) => {
    navigator.clipboard.writeText(hash)
    message.success("هش کامیت کپی شد")
  }

  // Open commit in GitHub
  const openCommitUrl = (url) => {
    window.open(url, "_blank")
  }

  // Reload commits
  const handleReload = () => {
    fetchCommits(selectedBranch, currentRepo)
  }

  // Statistics
  const stats = {
    total: filteredCommits.length,
    features: filteredCommits.filter(c => c.type === "feature").length,
    fixes: filteredCommits.filter(c => c.type === "fix").length,
    totalAdditions: filteredCommits.reduce((sum, c) => sum + c.additions, 0),
    totalDeletions: filteredCommits.reduce((sum, c) => sum + c.deletions, 0),
  }

  // Table columns
  const columns = [
    {
      title: (
        <Flex align="center" gap={4}>
          <CodeOutlined />
          <span>کامیت</span>
        </Flex>
      ),
      dataIndex: "message",
      key: "message",
      render: (text, record) => {
        const typeConfig = getCommitTypeConfig(record.type)
        return (
          <Flex vertical gap={4}>
            <Flex align="center" gap={8}>
              <Tag
                color={typeConfig.color}
                style={{ borderRadius: 4, fontSize: 11 }}
              >
                {typeConfig.icon}
                <span style={{ marginRight: 4 }}>{typeConfig.label}</span>
              </Tag>
              <Text
                  strong
                  style={{
                    fontSize: 13,
                    maxWidth: matches_sm ? 200 : 500,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    cursor: record.fullMessage && record.fullMessage !== text ? "pointer" : "default",
                  }}
                  onClick={() => record.fullMessage && record.fullMessage !== text && showFullMessage(record)}
                >
                  {text}
                </Text>
              {record.fullMessage  && (
                <Tooltip title="نمایش پیام کامل">
                  <Button
                    type="text"
                    size="small"
                    icon={<ExpandOutlined />}
                    onClick={() => showFullMessage(record)}
                    style={{ padding: 2, height: 20, width: 20, minWidth: 20 }}
                  />
                </Tooltip>
              )}
            </Flex>
            <Flex align="center" gap={8}>
              <Tooltip title="کپی هش">
                <Tag
                  style={{
                    cursor: "pointer",
                    fontFamily: "monospace",
                    fontSize: 11,
                    borderRadius: 4,
                  }}
                  onClick={() => copyHash(record.hash)}
                >
                  <CopyOutlined style={{ marginLeft: 4 }} />
                  {record.id}
                </Tag>
              </Tooltip>
              <Tag color="blue" style={{ borderRadius: 4, fontSize: 11 }}>
                <BranchesOutlined style={{ marginLeft: 4 }} />
                {record.branch}
              </Tag>
              {record.url && (
                <Tooltip title="مشاهده در GitHub">
                  <Tag
                    style={{ cursor: "pointer", borderRadius: 4, fontSize: 11 }}
                    onClick={() => openCommitUrl(record.url)}
                  >
                    <LinkOutlined style={{ marginLeft: 4 }} />
                    GitHub
                  </Tag>
                </Tooltip>
              )}
            </Flex>
          </Flex>
        )
      },
    },
    {
      title: (
        <Flex align="center" gap={4}>
          <UserOutlined />
          <span>نویسنده</span>
        </Flex>
      ),
      dataIndex: "author",
      key: "author",
      width: 150,
      render: (text, record) => (
        <Flex align="center" gap={8}>
          <Avatar
            size="small"
            src={record.authorAvatar}
            style={{
              background: !record.authorAvatar ? "linear-gradient(135deg, #667eea 0%, #764ba2 100%)" : undefined,
            }}
          >
            {!record.authorAvatar && text.charAt(0)}
          </Avatar>
          <Text style={{ fontSize: 12 }}>{text}</Text>
        </Flex>
      ),
    },
    {
      title: (
        <Flex align="center" gap={4}>
          <CalendarOutlined />
          <span>تاریخ</span>
        </Flex>
      ),
      dataIndex: "date",
      key: "date",
      width: 120,
      render: (text) => (
        <Text type="secondary" style={{ fontSize: 12 }}>
          <ClockCircleOutlined style={{ marginLeft: 4 }} />
          {formatDate(text)}
        </Text>
      ),
    },
    {
      title: (
        <Flex align="center" gap={4}>
          <EditOutlined />
          <span>تغییرات</span>
        </Flex>
      ),
      key: "changes",
      width: 150,
      render: (_, record) => (
        <Flex gap={8}>
          <Tag color="green" style={{ borderRadius: 4, fontSize: 11 }}>
            +{record.additions}
          </Tag>
          <Tag color="red" style={{ borderRadius: 4, fontSize: 11 }}>
            -{record.deletions}
          </Tag>
          <Tag style={{ borderRadius: 4, fontSize: 11 }}>
            {record.filesChanged} فایل
          </Tag>
        </Flex>
      ),
    },
  ]

  // Timeline view
  const renderTimelineView = () => (
    <Timeline
      mode={matches_sm ? "left" : "alternate"}
      items={filteredCommits.map((commit, index) => {
        const typeConfig = getCommitTypeConfig(commit.type)
        return {
          color: typeConfig.color,
          dot: (
            <div
              style={{
                width: 32,
                height: 32,
                borderRadius: "50%",
                background: `${typeConfig.color}20`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: typeConfig.color,
              }}
            >
              {typeConfig.icon}
            </div>
          ),
          children: (
            <Card
              size="small"
              style={{
                borderRadius: 12,
                border: "none",
                boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                marginBottom: 8,
                cursor: commit.url ? "pointer" : "default",
              }}
              bodyStyle={{ padding: 12 }}
              onClick={() => commit.url && openCommitUrl(commit.url)}
            >
              <Flex vertical gap={8}>
                <Flex justify="space-between" align="start" wrap="wrap" gap={8}>
                  <Text 
                    strong 
                    style={{ 
                      fontSize: 13,
                      cursor: commit.fullMessage && commit.fullMessage !== commit.message ? "pointer" : "default",
                    }}
                    onClick={(e) => {
                      if (commit.fullMessage && commit.fullMessage !== commit.message) {
                        e.stopPropagation()
                        showFullMessage(commit)
                      }
                    }}
                  >
                    {commit.message}
                    {commit.fullMessage && commit.fullMessage !== commit.message && (
                      <ExpandOutlined style={{ marginRight: 6, fontSize: 11, color: "#1890ff" }} />
                    )}
                  </Text>
                  <Tag color={typeConfig.color} style={{ borderRadius: 4 }}>
                    {typeConfig.label}
                  </Tag>
                </Flex>
                <Flex align="center" gap={12} wrap="wrap">
                  <Flex align="center" gap={4}>
                    <Avatar
                      size={20}
                      src={commit.authorAvatar}
                      style={{
                        background: !commit.authorAvatar ? "linear-gradient(135deg, #667eea 0%, #764ba2 100%)" : undefined,
                        fontSize: 10,
                      }}
                    >
                      {!commit.authorAvatar && commit.author.charAt(0)}
                    </Avatar>
                    <Text type="secondary" style={{ fontSize: 11 }}>
                      {commit.author}
                    </Text>
                  </Flex>
                  <Text type="secondary" style={{ fontSize: 11 }}>
                    <ClockCircleOutlined style={{ marginLeft: 4 }} />
                    {formatDate(commit.date)}
                  </Text>
                  <Tag
                    style={{ cursor: "pointer", fontFamily: "monospace", fontSize: 10 }}
                    onClick={(e) => {
                      e.stopPropagation()
                      copyHash(commit.hash)
                    }}
                  >
                    <CopyOutlined style={{ marginLeft: 4 }} />
                    {commit.id}
                  </Tag>
                </Flex>
                <Flex gap={8}>
                  <Tag color="blue" style={{ borderRadius: 4, fontSize: 10 }}>
                    <BranchesOutlined style={{ marginLeft: 4 }} />
                    {commit.branch}
                  </Tag>
                  {commit.url && (
                    <Tag style={{ borderRadius: 4, fontSize: 10 }}>
                      <LinkOutlined style={{ marginLeft: 4 }} />
                      GitHub
                    </Tag>
                  )}
                </Flex>
              </Flex>
            </Card>
          ),
        }
      })}
    />
  )

  return (
    <div style={{ padding: matches_sm ? 8 : 16 }}>
      {/* Header Card */}
      <Card
        style={{
          borderRadius: 16,
          border: "none",
          marginBottom: 16,
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          boxShadow: "0 4px 20px rgba(102, 126, 234, 0.3)",
        }}
        bodyStyle={{ padding: matches_sm ? 16 : 24 }}
      >
        <Flex justify="space-between" align="center" wrap="wrap" gap={16}>
          <Flex align="center" gap={12}>
            <div
              style={{
                width: 48,
                height: 48,
                borderRadius: 12,
                background: "rgba(255,255,255,0.2)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <GithubOutlined style={{ fontSize: 24, color: "#fff" }} />
            </div>
            <div>
              <Title level={4} style={{ margin: 0, color: "#fff" }}>
                تاریخچه کامیت‌ها
              </Title>
              <Flex align="center" gap={8}>
                <Text style={{ color: "rgba(255,255,255,0.8)", fontSize: 12 }}>
                  {currentRepo.owner}/{currentRepo.repo}
                </Text>
                <Tag 
                  color={currentRepo.color} 
                  style={{ borderRadius: 4, fontSize: 10, margin: 0 }}
                >
                  {currentRepo.label}
                </Tag>
              </Flex>
            </div>
          </Flex>
          <Flex gap={8} wrap="wrap">
            {/* Repository Selector */}
            <Select
              value={selectedRepo.id}
              onChange={handleRepoChange}
              style={{ minWidth: 140 }}
              dropdownStyle={{ minWidth: 180 }}
            >
              {GITHUB_REPOS.map(repo => (
                <Select.Option key={repo.id} value={repo.id}>
                  <Flex align="center" gap={6}>
                    <div
                      style={{
                        width: 8,
                        height: 8,
                        borderRadius: "50%",
                        background: repo.color,
                      }}
                    />
                    <span>{repo.label}</span>
                  </Flex>
                </Select.Option>
              ))}
            </Select>
            <Button
              icon={<LinkOutlined />}
              onClick={() => window.open(`https://github.com/${currentRepo.owner}/${currentRepo.repo}`, "_blank")}
              style={{
                borderRadius: 8,
                background: "rgba(255,255,255,0.2)",
                border: "none",
                color: "#fff",
              }}
            >
              {!matches_sm && "GitHub"}
            </Button>
            <Button
              icon={<ReloadOutlined spin={loading} />}
              onClick={handleReload}
              style={{
                borderRadius: 8,
                background: "rgba(255,255,255,0.2)",
                border: "none",
                color: "#fff",
              }}
            >
              {!matches_sm && "بروزرسانی"}
            </Button>
          </Flex>
        </Flex>
      </Card>

      {/* Statistics */}
      <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
        <Col xs={12} sm={6}>
          <Card
            size="small"
            style={{
              borderRadius: 12,
              border: "none",
              boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
            }}
          >
            <Statistic
              title={<Text type="secondary" style={{ fontSize: 12 }}>کل کامیت‌ها</Text>}
              value={stats.total}
              prefix={<HistoryOutlined style={{ color: "#667eea" }} />}
              valueStyle={{ color: "#667eea", fontSize: 20 }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card
            size="small"
            style={{
              borderRadius: 12,
              border: "none",
              boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
            }}
          >
            <Statistic
              title={<Text type="secondary" style={{ fontSize: 12 }}>ویژگی جدید</Text>}
              value={stats.features}
              prefix={<FileAddOutlined style={{ color: "#52c41a" }} />}
              valueStyle={{ color: "#52c41a", fontSize: 20 }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card
            size="small"
            style={{
              borderRadius: 12,
              border: "none",
              boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
            }}
          >
            <Statistic
              title={<Text type="secondary" style={{ fontSize: 12 }}>خطوط اضافه شده</Text>}
              value={stats.totalAdditions}
              prefix={<Text style={{ color: "#52c41a" }}>+</Text>}
              valueStyle={{ color: "#52c41a", fontSize: 20 }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card
            size="small"
            style={{
              borderRadius: 12,
              border: "none",
              boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
            }}
          >
            <Statistic
              title={<Text type="secondary" style={{ fontSize: 12 }}>خطوط حذف شده</Text>}
              value={stats.totalDeletions}
              prefix={<Text style={{ color: "#ff4d4f" }}>-</Text>}
              valueStyle={{ color: "#ff4d4f", fontSize: 20 }}
            />
          </Card>
        </Col>
      </Row>

      {/* Filters Card */}
      <Card
        size="small"
        style={{
          borderRadius: 12,
          border: "none",
          boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
          marginBottom: 16,
        }}
        bodyStyle={{ padding: matches_sm ? 12 : 16 }}
      >
        <Flex justify="space-between" align="center" wrap="wrap" gap={12}>
          <Flex gap={8} wrap="wrap" style={{ flex: 1 }}>
            <Search
              placeholder="جستجو در کامیت‌ها..."
              allowClear
              onChange={(e) => setSearchText(e.target.value)}
              style={{ width: matches_sm ? "100%" : 250, borderRadius: 8 }}
            />
            <Select
              value={selectedBranch}
              onChange={setSelectedBranch}
              style={{ width: matches_sm ? "100%" : 180 }}
              placeholder="انتخاب شاخه"
              loading={branches.length === 0}
              suffixIcon={<BranchesOutlined />}
            >
              {branches.map(b => (
                <Select.Option key={b} value={b}>
                  <Flex align="center" gap={4}>
                    <BranchesOutlined />
                    <span>{b}</span>
                  </Flex>
                </Select.Option>
              ))}
            </Select>
            <Select
              value={perPage}
              onChange={setPerPage}
              style={{ width: matches_sm ? "100%" : 120 }}
              placeholder="تعداد"
            >
              <Select.Option value={10}>10 کامیت</Select.Option>
              <Select.Option value={30}>30 کامیت</Select.Option>
              <Select.Option value={50}>50 کامیت</Select.Option>
              <Select.Option value={100}>100 کامیت</Select.Option>
            </Select>
          </Flex>
          <Segmented
            value={viewMode}
            onChange={setViewMode}
            options={[
              {
                value: "table",
                icon: <UnorderedListOutlined />,
                label: !matches_sm ? "جدول" : "",
              },
              {
                value: "timeline",
                icon: <AppstoreOutlined />,
                label: !matches_sm ? "تایم‌لاین" : "",
              },
            ]}
            style={{ borderRadius: 8 }}
          />
        </Flex>
      </Card>

      {/* Content Card */}
      <Card
        size="small"
        style={{
          borderRadius: 12,
          border: "none",
          boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
        }}
        bodyStyle={{ padding: matches_sm ? 8 : 16 }}
      >
        <Spin spinning={loading}>
          {filteredCommits.length === 0 ? (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={
                <Text type="secondary">کامیتی یافت نشد</Text>
              }
            />
          ) : viewMode === "table" ? (
            <Table
              columns={columns}
              dataSource={filteredCommits}
              rowKey="id"
              pagination={{
                pageSize: 10,
                showSizeChanger: true,
                showTotal: (total) => `${total} کامیت`,
              }}
              scroll={{ x: 700 }}
              size="small"
            />
          ) : (
            <div style={{ padding: matches_sm ? 8 : 24 }}>
              {renderTimelineView()}
            </div>
          )}
        </Spin>
      </Card>

      {/* Full Message Modal */}
      <Modal
        title={
          <Flex align="center" gap={8}>
            <MessageOutlined style={{ color: "#667eea" }} />
            <span>پیام کامیت</span>
          </Flex>
        }
        open={messageModalVisible}
        onCancel={() => setMessageModalVisible(false)}
        footer={
          <Flex justify="space-between" align="center">
            <Flex gap={8}>
              {selectedCommit && (
                <>
                  <Tag
                    style={{ cursor: "pointer", fontFamily: "monospace" }}
                    onClick={() => copyHash(selectedCommit.hash)}
                  >
                    <CopyOutlined style={{ marginLeft: 4 }} />
                    {selectedCommit.id}
                  </Tag>
                  <Tag color="blue">
                    <BranchesOutlined style={{ marginLeft: 4 }} />
                    {selectedCommit.branch}
                  </Tag>
                </>
              )}
            </Flex>
            <Flex gap={8}>
              {selectedCommit?.url && (
                <Button
                  icon={<LinkOutlined />}
                  onClick={() => openCommitUrl(selectedCommit.url)}
                >
                  مشاهده در GitHub
                </Button>
              )}
              <Button onClick={() => setMessageModalVisible(false)}>
                بستن
              </Button>
            </Flex>
          </Flex>
        }
        width={matches_sm ? "95%" : 600}
        centered
      >
        {selectedCommit && (
          <div>
            {/* Commit Type & Title */}
            <Flex align="center" gap={8} style={{ marginBottom: 16 }}>
              <Tag
                color={getCommitTypeConfig(selectedCommit.type).color}
                style={{ borderRadius: 4 }}
              >
                {getCommitTypeConfig(selectedCommit.type).icon}
                <span style={{ marginRight: 4 }}>
                  {getCommitTypeConfig(selectedCommit.type).label}
                </span>
              </Tag>
              <Tag color={currentRepo.color} style={{ borderRadius: 4 }}>
                {currentRepo.label}
              </Tag>
            </Flex>

            {/* Full Message */}
            <Card
              size="small"
              style={{
                background: "#f6f8fa",
                borderRadius: 8,
                border: "1px solid #e1e4e8",
                marginBottom: 16,
              }}
            >
              <pre
                style={{
                  margin: 0,
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                  fontFamily: "Vazir, monospace",
                  fontSize: 13,
                  lineHeight: 1.8,
                  direction: "rtl",
                  textAlign: "right",
                }}
              >
                {selectedCommit.fullMessage}
              </pre>
            </Card>

            {/* Author & Date Info */}
            <Flex justify="space-between" align="center" wrap="wrap" gap={8}>
              <Flex align="center" gap={8}>
                <Avatar
                  size={28}
                  src={selectedCommit.authorAvatar}
                  style={{
                    background: !selectedCommit.authorAvatar
                      ? "linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
                      : undefined,
                  }}
                >
                  {!selectedCommit.authorAvatar && selectedCommit.author.charAt(0)}
                </Avatar>
                <div>
                  <Text strong style={{ fontSize: 13 }}>
                    {selectedCommit.author}
                  </Text>
                  <br />
                  <Text type="secondary" style={{ fontSize: 11 }}>
                    {selectedCommit.authorEmail}
                  </Text>
                </div>
              </Flex>
              <Text type="secondary" style={{ fontSize: 12 }}>
                <ClockCircleOutlined style={{ marginLeft: 4 }} />
                {formatDate(selectedCommit.date)}
                <span style={{ marginRight: 8, color: "#999" }}>
                  ({new Date(selectedCommit.date).toLocaleDateString("fa-IR")} -{" "}
                  {new Date(selectedCommit.date).toLocaleTimeString("fa-IR")})
                </span>
              </Text>
            </Flex>
          </div>
        )}
      </Modal>
    </div>
  )
}

export default GitCommits
